import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import PinModal from '../components/PinModal';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, CreditCard, User, Calendar, DollarSign, CheckCircle, Clock,
  Plus, Trash2, Edit, FileText, XCircle, AlertTriangle, Stethoscope,
  FlaskConical, Heart, Scissors, Ban, Percent, Search, Tag, Building2, Printer, Send, AlertOctagon
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  consultation: Stethoscope,
  lab_test: FlaskConical,
  nursing: Heart,
  procedure: Scissors
};

const CATEGORY_LABELS = {
  consultation: 'Consultation',
  lab_test: 'Lab/Test',
  nursing: 'Nursing',
  procedure: 'Procedure'
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  finalized: { label: 'Finalized', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Send },
  partially_paid: { label: 'Partial', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertOctagon },
  void: { label: 'Void', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  Unpaid: { label: 'Unpaid', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  Paid: { label: 'Paid', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle }
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isDoctor, isNurse, user } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modals
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Service search
  const [serviceSearch, setServiceSearch] = useState('');
  
  // Form data
  const [selectedService, setSelectedService] = useState(null);
  const [lineItemData, setLineItemData] = useState({
    service_id: '',
    description: '',
    quantity: 1,
    unit_price: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [adjustmentData, setAdjustmentData] = useState({
    amount: '',
    reason: ''
  });
  const [discountData, setDiscountData] = useState({
    item_id: '',
    item_description: '',
    item_total: 0,
    discount_type: 'percentage',
    discount_value: '',
    discount_reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [invoiceRes, servicesRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/services`, { params: { active_only: true, billable_only: true } })
      ]);
      setInvoice(invoiceRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      toast.error('Failed to load invoice');
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD' }).format(amount || 0);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter services based on search
  const filteredServices = services.filter(service => 
    service.name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
    service.category.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setSelectedService(service);
      setLineItemData({
        service_id: serviceId,
        description: service.name,
        quantity: 1,
        unit_price: service.default_price.toString()
      });
    }
  };

  const handleAddLineItem = async (e) => {
    e.preventDefault();
    if (!lineItemData.service_id) {
      toast.error('Please select a service from the catalogue');
      return;
    }
    
    setSaving(true);
    try {
      // Nurses and Doctors must use catalogue price
      const userRole = user?.role;
      const service = services.find(s => s.id === lineItemData.service_id);
      
      if ((userRole === 'nurse' || userRole === 'doctor') && service) {
        // Force catalogue price for nurses and doctors
        lineItemData.unit_price = service.default_price.toString();
      }
      
      await axios.post(`${API}/invoices/${id}/line-items`, {
        service_id: lineItemData.service_id,
        description: lineItemData.description,
        quantity: parseInt(lineItemData.quantity),
        unit_price: parseFloat(lineItemData.unit_price)
      });
      toast.success('Service added to invoice');
      setShowAddItemModal(false);
      setLineItemData({ service_id: '', description: '', quantity: 1, unit_price: '' });
      setSelectedService(null);
      setServiceSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add service');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLineItem = (itemId, itemDescription) => {
    setPendingAction({
      type: 'remove_item',
      itemId,
      itemDescription
    });
    setShowPinModal(true);
  };

  const openDiscountModal = (item) => {
    setDiscountData({
      item_id: item.id,
      item_description: item.description,
      item_total: item.total,
      discount_type: 'percentage',
      discount_value: item.discount_amount > 0 ? '' : '',
      discount_reason: item.discount_reason || ''
    });
    setShowDiscountModal(true);
  };

  const handleApplyDiscount = (e) => {
    e.preventDefault();
    if (!discountData.discount_value || !discountData.discount_reason) {
      toast.error('Please fill in all discount fields');
      return;
    }
    setPendingAction({ type: 'apply_discount' });
    setShowDiscountModal(false);
    setShowPinModal(true);
  };

  const executePinAction = async (pin) => {
    setSaving(true);
    try {
      if (pendingAction.type === 'remove_item') {
        await axios.delete(`${API}/invoices/${id}/line-items/${pendingAction.itemId}?pin=${pin}`);
        toast.success('Item removed');
      } else if (pendingAction.type === 'apply_adjustment') {
        await axios.put(
          `${API}/invoices/${id}/adjustment?adjustment_amount=${adjustmentData.amount}&adjustment_reason=${encodeURIComponent(adjustmentData.reason)}&pin=${pin}`
        );
        toast.success('Adjustment applied');
        setShowAdjustmentModal(false);
        setAdjustmentData({ amount: '', reason: '' });
      } else if (pendingAction.type === 'apply_discount') {
        await axios.post(`${API}/invoices/${id}/line-items/${discountData.item_id}/discount`, {
          discount_type: discountData.discount_type,
          discount_value: parseFloat(discountData.discount_value),
          discount_reason: discountData.discount_reason,
          pin: pin
        });
        toast.success('Discount applied');
        setDiscountData({ item_id: '', item_description: '', item_total: 0, discount_type: 'percentage', discount_value: '', discount_reason: '' });
      } else if (pendingAction.type === 'void_invoice') {
        await axios.put(`${API}/invoices/${id}/status`, {
          status: 'void',
          pin: pin
        });
        toast.success('Invoice voided');
      }
      fetchData();
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
      setPendingAction(null);
    }
  };

  const handleFinalize = async () => {
    if (!invoice.line_items?.length) {
      toast.error('Cannot finalize invoice without line items');
      return;
    }
    
    if (!window.confirm('Are you sure you want to finalize this invoice? This action cannot be undone.')) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/invoices/${id}/status`, { status: 'finalized' });
      toast.success('Invoice finalized');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/invoices/${id}/status`, {
        status: 'paid',
        payment_method: paymentMethod
      });
      toast.success('Invoice marked as paid');
      setShowPaymentModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = () => {
    if (!window.confirm('Are you sure you want to void this invoice? This action cannot be undone.')) return;
    setPendingAction({ type: 'void_invoice' });
    setShowPinModal(true);
  };

  const handleApplyAdjustment = (e) => {
    e.preventDefault();
    setPendingAction({ type: 'apply_adjustment' });
    setShowPinModal(true);
  };

  // Phase 6.4: Handle status changes for insurance invoices
  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      await axios.put(`${API}/invoices/${id}/status`, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update status to ${newStatus}`);
    } finally {
      setSaving(false);
    }
  };

  // Permissions based on Phase 6.3 + 6.4
  const userRole = user?.role;
  const canAddItems = invoice?.status === 'draft' && ['owner', 'reception', 'doctor', 'nurse'].includes(userRole);
  const canApplyDiscount = invoice?.status === 'draft' && ['owner', 'reception'].includes(userRole);
  const canRemoveItems = invoice?.status === 'draft' && ['owner', 'reception'].includes(userRole);
  const canFinalize = ['owner', 'reception'].includes(userRole) && invoice?.status === 'draft';
  const canMarkPaid = ['owner', 'reception'].includes(userRole) && ['finalized', 'submitted', 'partially_paid'].includes(invoice?.status);
  const canVoid = isOwner && ['finalized', 'paid', 'submitted', 'partially_paid', 'rejected'].includes(invoice?.status);
  const canEditPrice = invoice?.status === 'draft' && ['owner', 'reception'].includes(userRole);
  // Phase 6.4: Insurance permissions
  const isInsuranceInvoice = invoice?.is_insurance_invoice;
  const canMarkSubmitted = ['owner', 'reception'].includes(userRole) && isInsuranceInvoice && invoice?.status === 'finalized';
  const canMarkRejected = isOwner && isInsuranceInvoice && ['submitted', 'partially_paid'].includes(invoice?.status);
  const canMarkPartialPaid = ['owner', 'reception'].includes(userRole) && isInsuranceInvoice && invoice?.status === 'submitted';

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!invoice) return null;

  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <Layout>
      <div className="page-container" data-testid="invoice-detail-page">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/billing')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Billing
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-lg text-slate-600">{invoice.invoice_number}</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusConfig.color}`}>
                  <StatusIcon className="w-4 h-4 inline mr-1" />
                  {statusConfig.label}
                </span>
                {/* Phase 6.4: Insurance Badge */}
                {isInsuranceInvoice && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 border border-blue-200">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Insurance
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <User className="w-4 h-4 text-slate-400" />
                <h1 className="text-xl font-bold text-slate-900">{invoice.patient_name}</h1>
              </div>
              {/* Phase 6.4: Insurance Details */}
              {isInsuranceInvoice && invoice.insurance_provider_name && (
                <div className="mt-2 text-sm text-slate-600">
                  <span className="font-medium">{invoice.insurance_provider_name}</span>
                  <span className="mx-2">•</span>
                  <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{invoice.insurance_provider_code}</span>
                  <span className="mx-2">•</span>
                  <span>Policy: {invoice.policy_number}</span>
                  {invoice.member_name && (
                    <span className="ml-2">(Member: {invoice.member_name})</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Print Button */}
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="border-2"
              >
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              {canVoid && (
                <Button
                  variant="outline"
                  onClick={handleVoid}
                  className="border-2 text-red-600 hover:bg-red-50"
                  disabled={saving}
                >
                  <Ban className="w-4 h-4 mr-1" />
                  Void
                </Button>
              )}
              {canFinalize && (
                <Button
                  onClick={handleFinalize}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving || !invoice.line_items?.length}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Finalize
                </Button>
              )}
              {/* Phase 6.4: Mark Submitted for Insurance */}
              {canMarkSubmitted && (
                <Button
                  onClick={() => handleStatusChange('submitted')}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={saving}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Mark Submitted
                </Button>
              )}
              {/* Phase 6.4: Mark Rejected for Insurance */}
              {canMarkRejected && (
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange('rejected')}
                  className="border-2 text-red-600 hover:bg-red-50"
                  disabled={saving}
                >
                  <AlertOctagon className="w-4 h-4 mr-1" />
                  Rejected
                </Button>
              )}
              {/* Phase 6.4: Mark Partially Paid for Insurance */}
              {canMarkPartialPaid && (
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange('partially_paid')}
                  className="border-2 text-amber-600 hover:bg-amber-50"
                  disabled={saving}
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Partial Paid
                </Button>
              )}
              {canMarkPaid && (
                <Button
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={saving}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Paid
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Line Items */}
            <div className="card" data-testid="line-items-section">
              <div className="p-4 border-b-2 border-slate-200 flex items-center justify-between">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Line Items ({invoice.line_items?.length || 0})
                </h2>
                {canAddItems && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddItemModal(true)}
                    className="border-2"
                    data-testid="add-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Service
                  </Button>
                )}
              </div>
              
              {invoice.line_items?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {invoice.line_items.map((item) => {
                    const CategoryIcon = CATEGORY_ICONS[item.category] || CreditCard;
                    const hasDiscount = item.discount_amount > 0;
                    
                    return (
                      <div key={item.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center mt-1">
                              <CategoryIcon className="w-4 h-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{item.description}</p>
                              <p className="text-sm text-slate-500">
                                {item.quantity} × {formatCurrency(item.unit_price)}
                                <span className="mx-2">•</span>
                                <span className="capitalize">{CATEGORY_LABELS[item.category] || item.category}</span>
                              </p>
                              {/* Phase 6.4: Show insurer code for insurance invoices */}
                              {isInsuranceInvoice && item.insurer_code && (
                                <p className="text-xs text-blue-600 mt-1">
                                  <Building2 className="w-3 h-3 inline mr-1" />
                                  Insurer Code: <span className="font-mono">{item.insurer_code}</span>
                                  {item.insurer_price > 0 && (
                                    <span className="ml-2">• Insurer Rate: {formatCurrency(item.insurer_price)}</span>
                                  )}
                                </p>
                              )}
                              {/* Phase 6.4: Manual review flag */}
                              {isInsuranceInvoice && item.requires_manual_review && (
                                <p className="text-xs text-amber-600 mt-1">
                                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                                  Requires manual review - no insurer code mapped
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                Added by {item.added_by_name} ({item.added_by_role})
                              </p>
                              {hasDiscount && (
                                <div className="mt-1 text-xs">
                                  <span className="text-red-600 font-medium">
                                    Discount: -{formatCurrency(item.discount_amount)}
                                  </span>
                                  {item.discount_reason && (
                                    <span className="text-slate-500 ml-2">({item.discount_reason})</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              {hasDiscount ? (
                                <>
                                  <p className="text-sm text-slate-400 line-through">{formatCurrency(item.total)}</p>
                                  <p className="font-semibold text-green-600">{formatCurrency(item.final_total)}</p>
                                </>
                              ) : (
                                <p className="font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                              )}
                              {/* Phase 6.4: Show patient/insurer portions for insurance invoices */}
                              {isInsuranceInvoice && (
                                <div className="text-xs mt-1">
                                  <span className="text-slate-500">Patient: {formatCurrency(item.patient_portion || 0)}</span>
                                  <span className="mx-1">|</span>
                                  <span className="text-blue-600">Insurer: {formatCurrency(item.insurer_portion || 0)}</span>
                                </div>
                              )}
                            </div>
                            {invoice.status === 'draft' && (
                              <div className="flex gap-1">
                                {canApplyDiscount && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDiscountModal(item)}
                                    className="text-amber-600 hover:bg-amber-50"
                                    title="Apply Discount"
                                  >
                                    <Tag className="w-4 h-4" />
                                  </Button>
                                )}
                                {canRemoveItems && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveLineItem(item.id, item.description)}
                                    className="text-red-600 hover:bg-red-50"
                                    title="Remove Item"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600 font-medium">No line items</p>
                  <p className="text-sm text-slate-500">Select services from the catalogue to add</p>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="card p-6" data-testid="totals-section">
              <div className="space-y-3">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                {(invoice.adjustment_amount !== 0 || (canApplyDiscount && invoice.status === 'draft')) && (
                  <div className="flex justify-between items-center text-slate-600">
                    <div className="flex items-center gap-2">
                      <span>Invoice Adjustment</span>
                      {canApplyDiscount && invoice.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAdjustmentData({
                              amount: invoice.adjustment_amount?.toString() || '0',
                              reason: invoice.adjustment_reason || ''
                            });
                            setShowAdjustmentModal(true);
                          }}
                          className="text-blue-600 hover:bg-blue-50 h-6 px-2"
                        >
                          <Percent className="w-3 h-3 mr-1" />
                          {invoice.adjustment_amount ? 'Edit' : 'Add'}
                        </Button>
                      )}
                    </div>
                    <span className={invoice.adjustment_amount < 0 ? 'text-red-600' : 'text-green-600'}>
                      {invoice.adjustment_amount >= 0 ? '+' : ''}{formatCurrency(invoice.adjustment_amount)}
                    </span>
                  </div>
                )}
                {invoice.adjustment_reason && (
                  <p className="text-xs text-slate-500 pl-4">Reason: {invoice.adjustment_reason}</p>
                )}
                <div className="border-t-2 border-slate-200 pt-3 flex justify-between">
                  <span className="font-bold text-slate-900 text-lg">Grand Total</span>
                  <span className="font-bold text-slate-900 text-2xl">{formatCurrency(invoice.grand_total)}</span>
                </div>
                {/* Phase 6.4: Patient/Insurer split for insurance invoices */}
                {isInsuranceInvoice && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">Patient Responsibility</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(invoice.patient_total || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600 flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        Insurer Portion
                      </span>
                      <span className="font-semibold text-blue-600">{formatCurrency(invoice.insurer_total || 0)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Role Info Banner */}
            {invoice.status === 'draft' && (
              <div className={`card p-4 ${isDoctor ? 'bg-blue-50 border-blue-200' : isNurse ? 'bg-pink-50 border-pink-200' : 'bg-slate-50'}`}>
                <p className="text-sm font-medium text-slate-700">
                  {isDoctor && "As a Doctor, you can add services but cannot modify prices or finalize."}
                  {isNurse && "As a Nurse, you can add billable services but cannot modify prices or finalize."}
                  {(isOwner || userRole === 'reception') && "You can add services, apply discounts, and finalize this invoice."}
                </p>
              </div>
            )}
            
            {/* Invoice Info */}
            <div className="card p-4 space-y-4">
              <h3 className="font-semibold text-slate-900">Invoice Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Created</span>
                  <span className="text-slate-900">{formatDateTime(invoice.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Created By</span>
                  <span className="text-slate-900">{invoice.created_by_name}</span>
                </div>
                {invoice.finalized_at && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Finalized</span>
                      <span className="text-slate-900">{formatDateTime(invoice.finalized_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Finalized By</span>
                      <span className="text-slate-900">{invoice.finalized_by_name}</span>
                    </div>
                  </>
                )}
                {invoice.paid_at && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Paid</span>
                      <span className="text-slate-900">{formatDateTime(invoice.paid_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Payment Method</span>
                      <span className="text-slate-900">{invoice.payment_method}</span>
                    </div>
                  </>
                )}
                {invoice.voided_at && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Voided</span>
                      <span className="text-red-600">{formatDateTime(invoice.voided_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Voided By</span>
                      <span className="text-red-600">{invoice.voided_by_name}</span>
                    </div>
                  </>
                )}
                {invoice.encounter_id && (
                  <div className="pt-3 border-t border-slate-200">
                    <a 
                      href={`/encounters/${invoice.encounter_id}`}
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Stethoscope className="w-4 h-4" />
                      View Linked Encounter
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="card p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Notes</h3>
                <p className="text-sm text-slate-600">{invoice.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Line Item Modal - With Searchable Service Dropdown */}
        <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
          <DialogContent className="sm:max-w-lg border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" />
                Add Service from Catalogue
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddLineItem} className="space-y-4 pt-4">
              {/* Service Search */}
              <div>
                <Label>Search Service</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Type to search services..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    className="pl-10"
                    data-testid="service-search-input"
                  />
                </div>
              </div>
              
              {/* Service List */}
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {filteredServices.length > 0 ? (
                  filteredServices.map((service) => {
                    const Icon = CATEGORY_ICONS[service.category] || CreditCard;
                    const isSelected = selectedService?.id === service.id;
                    return (
                      <div
                        key={service.id}
                        onClick={() => handleServiceSelect(service.id)}
                        className={`p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${isSelected ? 'bg-blue-50 border-blue-200' : ''}`}
                        data-testid={`service-option-${service.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4 text-slate-500" />
                          <div>
                            <p className="font-medium text-slate-900">{service.name}</p>
                            <p className="text-xs text-slate-500 capitalize">{CATEGORY_LABELS[service.category]}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{formatCurrency(service.default_price)}</span>
                          {isSelected && <CheckCircle className="w-4 h-4 text-blue-600" />}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-slate-500">
                    {serviceSearch ? 'No services match your search' : 'No services available'}
                  </div>
                )}
              </div>
              
              {/* Selected Service Details */}
              {selectedService && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900">Selected: {selectedService.name}</p>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label htmlFor="quantity">Quantity</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={lineItemData.quantity}
                        onChange={(e) => setLineItemData({ ...lineItemData, quantity: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit_price">Unit Price</Label>
                      <Input
                        id="unit_price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={lineItemData.unit_price}
                        onChange={(e) => setLineItemData({ ...lineItemData, unit_price: e.target.value })}
                        required
                        disabled={!canEditPrice}
                        className={!canEditPrice ? 'bg-slate-100' : ''}
                      />
                      {!canEditPrice && (
                        <p className="text-xs text-slate-500 mt-1">Price locked to catalogue</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddItemModal(false);
                    setSelectedService(null);
                    setServiceSearch('');
                  }}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving || !selectedService}
                >
                  {saving ? 'Adding...' : 'Add Service'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Line Item Discount Modal */}
        <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-amber-600" />
                Apply Line Item Discount
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleApplyDiscount} className="space-y-4 pt-4">
              <div className="bg-slate-50 rounded-lg p-3 border">
                <p className="font-medium text-slate-900">{discountData.item_description}</p>
                <p className="text-sm text-slate-600">Item Total: {formatCurrency(discountData.item_total)}</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Discounts require PIN verification and are logged for audit.
              </div>
              
              <div>
                <Label>Discount Type</Label>
                <Select
                  value={discountData.discount_type}
                  onValueChange={(value) => setDiscountData({ ...discountData, discount_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="discount_value">
                  {discountData.discount_type === 'percentage' ? 'Percentage (0-100)' : 'Amount'}
                </Label>
                <Input
                  id="discount_value"
                  type="number"
                  min="0"
                  max={discountData.discount_type === 'percentage' ? 100 : discountData.item_total}
                  step={discountData.discount_type === 'percentage' ? '1' : '0.01'}
                  value={discountData.discount_value}
                  onChange={(e) => setDiscountData({ ...discountData, discount_value: e.target.value })}
                  placeholder={discountData.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="discount_reason">Reason (required)</Label>
                <Input
                  id="discount_reason"
                  value={discountData.discount_reason}
                  onChange={(e) => setDiscountData({ ...discountData, discount_reason: e.target.value })}
                  placeholder="e.g., Loyalty discount, Staff discount"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDiscountModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={saving}
                >
                  Continue with PIN
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Record Payment
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMarkPaid} className="space-y-4 pt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Amount Due</span>
                  <span className="text-2xl font-bold text-slate-900">{formatCurrency(invoice.grand_total)}</span>
                </div>
              </div>
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  disabled={saving}
                >
                  {saving ? 'Processing...' : 'Confirm Payment'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Adjustment Modal */}
        <Dialog open={showAdjustmentModal} onOpenChange={setShowAdjustmentModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Percent className="w-5 h-5 text-amber-600" />
                Invoice Adjustment
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleApplyAdjustment} className="space-y-4 pt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Adjustments require PIN verification and are logged for audit.
              </div>
              <div>
                <Label htmlFor="adjustment_amount">Adjustment Amount</Label>
                <Input
                  id="adjustment_amount"
                  type="number"
                  step="0.01"
                  value={adjustmentData.amount}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, amount: e.target.value })}
                  placeholder="Use negative for discount (e.g., -500)"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Use negative value for discounts</p>
              </div>
              <div>
                <Label htmlFor="adjustment_reason">Reason (required)</Label>
                <Input
                  id="adjustment_reason"
                  value={adjustmentData.reason}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                  placeholder="Reason for adjustment"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAdjustmentModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={saving}
                >
                  Continue with PIN
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* PIN Modal */}
        <PinModal
          isOpen={showPinModal}
          onClose={() => {
            setShowPinModal(false);
            setPendingAction(null);
          }}
          onVerify={executePinAction}
          title={
            pendingAction?.type === 'remove_item' ? 'Remove Line Item' :
            pendingAction?.type === 'apply_adjustment' ? 'Apply Adjustment' :
            pendingAction?.type === 'apply_discount' ? 'Apply Discount' :
            pendingAction?.type === 'void_invoice' ? 'Void Invoice' : 'Verify PIN'
          }
          description={
            pendingAction?.type === 'remove_item' 
              ? `Enter PIN to remove "${pendingAction?.itemDescription}"`
              : pendingAction?.type === 'apply_adjustment'
              ? 'Enter PIN to apply this adjustment'
              : pendingAction?.type === 'apply_discount'
              ? 'Enter PIN to apply this discount'
              : pendingAction?.type === 'void_invoice'
              ? 'Enter PIN to void this invoice'
              : 'Enter your admin PIN'
          }
          loading={saving}
        />
      </div>
    </Layout>
  );
};

export default InvoiceDetail;
